import React, { useRef, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useFormikContext } from 'formik';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tooltip,
  useDisclosure,
  usePrevious,
} from '@chakra-ui/core';

import { TabForm } from '../../../../common/components/Tabs';
import { FormSection, FormSubmitButton } from '../../../../common/components/Form/Form';
import {
  Label,
  RadioGroup,
  RadioInput,
  TextInput,
  SwitchInput,
} from '../../../../common/components/Form/FormInputs';
import { SelectInput } from '../../../../common/components/Form/FormSelectInput';
import { DateInput } from '../../../../common/components/Form/FormDateInput';
import { LockedInput } from '../../../../common/components/Form/FormLockedInput';
import {
  Notice,
  NoticeList,
  NoticeText,
  NoticeThinText,
} from '../../../../common/components/Notice';
import { PrimaryButton, SecondaryButton } from '../../../../common/components/Buttons';
import {
  QuoteUploadFilename,
  QuoteUploadWrapper,
} from '../../../../common/components/QuoteUpload/QuoteUpload';
import Exclude from '../../../../roleBasedAccess/Exclude';
import Can from '../../../../roleBasedAccess/Can';
import { BlocFormik } from '../../../../common/components/Form/BlocFormik';

export const BlocATab1 = ({
  blocA: { error, pending, folder = {}, ...initialValues },
  operators,
  companies = [],
  onSubmit,
}) => {
  const formikRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isPublishErgoProjectPlaceModalOpen,
    onOpen: onPublishErgoProjectPlaceModalOpen,
    onClose: onPublishErgoProjectPlaceClose,
  } = useDisclosure();
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setErrors(error && error.errors);
    }
  }, [error]);

  useEffect(() => {
    if (!initialValues.qualityControllerUserId && initialValues.defaultQualityControllerUserId) {
      const defaultController = getPositionnableQualityControllers(initialValues).find(
        controller => controller.id === initialValues.defaultQualityControllerUserId,
      );
      if (defaultController) {
        formikRef.current.setFieldValue('qualityControllerUserId', {
          label: `${defaultController.firstName} ${defaultController.lastName}`,
          value: defaultController.id,
        });
      }
    }
  }, [initialValues]);

  const onModalClose = () => {
    onClose();
    setSuccess(false);
  };

  const onPublishErgoProjectPlaceModalClose = () => {
    onPublishErgoProjectPlaceClose();
    setSuccess(false);
  };

  const isQualityControllerFilled = values =>
    !!values.qualityControllerUserId || values.ignoreQualityControl;
  const isQualityControllerDisabled = values => values.ignoreQualityControl;
  const isPublishErgoProjectPlaceModalNeeded =
    folder.folderType === 'AMOc' || folder.folderType === 'AMOe';
  const isTypeOfDisabled = !initialValues.bloc_0QuoteRequestPerimeter?.includes('works');
  const isPositionnedCompanyDisabled = values =>
    isTypeOfDisabled || !values.typeOfVisit || values.typeOfVisit === 'solo'; 
  const isRecipientContactedAtDisabled = values =>
    !values.ergoUserId ||
    !values.ergoUserKind ||
    (['existing_quote', 'joint'].includes(values.typeOfVisit) && !values.positionedCompany) ||
    (initialValues.bloc_0QuoteRequestPerimeter?.includes('works') && !values.typeOfVisit);
  const isIsRecipientContactedDisabled = values =>
    !values.ergoUserId ||
    !values.ergoUserKind ||
    (['existing_quote', 'joint'].includes(values.typeOfVisit) && !values.positionedCompany) ||
    (initialValues.bloc_0QuoteRequestPerimeter?.includes('works') && !values.typeOfVisit);
  const isHousingVisitedAtDisabled = values =>
    values.isRecipientContacted !== 'yes' || !values.ergoUserId || !values.ergoUserKind;
  const getComparableDate = value => {
    if (!value) return null;
    if (value instanceof Date) return format(value, 'yyyy-MM-dd');
    const [day, month, year] = value.split('/');
    return year ? `${year}-${month}-${day}` : value;
  };
  const hasHousingVisitedAtChanged = values => {
    return (
      getComparableDate(initialValues.housingVisitedAt) !==
      getComparableDate(values.housingVisitedAt)
    );
  };
  const isHousingVisitedAtTimeRequired = values =>
    Boolean(
      values.isRecipientContacted === 'yes' &&
        values.housingVisitedAt &&
        (initialValues.housingVisitedAtTime ||
          !initialValues.housingVisitedAt ||
          hasHousingVisitedAtChanged(values)),
    );
  const isVisitReminderSentAtDisabled = values =>
    !values.housingVisitedAt || !values.ergoUserId || !values.ergoUserKind;
  const isHousingVisitDatePassed = values =>
    values.housingVisitedAt ? new Date() >= values.housingVisitedAt : false;
  const getErgoQualification = values => {
    if (values?.ergoQualification?.includes('interne')) return 'interne';
    if (values?.ergoQualification?.includes('cabinet')) return 'externe';
    return null;
  };
  const isProjectHavingPositionableErgo = values =>
    values.ergoProjectHasEligibleUsers ||
    !values.isPublishedOnErgoProjectPlace ||
    values.ergoUserId;
  const getPositionnableErgoUsers = values => {
    return values.positionnableErgoUsers;
  };
  const getPositionnableQualityControllers = values => {
    return values.positionnableQualityControllers;
  };
  const isPublishOnErgoProjectPlaceDisabled = values =>
    values.isPublishedOnErgoProjectPlace ||
    (!isTypeOfDisabled && !values.typeOfVisit) ||
    (values.typeOfVisit === 'existing_quote' && !values.positionedCompany);

  const isPositionableErgoDisabled = values =>
    values.typeOfVisit === 'existing_quote' && !values.positionedCompany;

  const filterCompanies = (typeOfVisit, excludedCompanies = []) => {
    if (typeOfVisit === 'existing_quote' || typeOfVisit === 'joint') {
      return companies?.map(company => ({ label: company.name, value: company.id }));
    }

    return companies
      ?.filter(company => {
        return company.departments
          .map(dept => dept.slice(0, 2))
          .includes(folder?.housingToAdapt?.postalCode?.slice(0, 2));
      })
      .filter(company => !excludedCompanies.includes(company.id))
      .map(company => ({ label: company.name, value: company.id }));
  };

  return (
    <BlocFormik
      innerRef={formikRef}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setTouched, setSubmitting }) => {
        return onSubmit({ ...initialValues, folder, ...values }).then(() => {
          setTouched({});
          setSubmitting(false);
        });
      }}
    >
      {formik => {
        const filteredCompanies = filterCompanies(
          formik.values.typeOfVisit,
          initialValues?.ignoredFromProjectCompanyIds?.map(id => Number(id)),
        );
        const isHousingVisitDateMissing =
          formik.values.isRecipientContacted === 'yes' && !formik.values.housingVisitedAt;
        const isHousingVisitTimeMissing =
          isHousingVisitedAtTimeRequired(formik.values) && !formik.values.housingVisitedAtTime;

        return (
          <TabForm onSubmit={formik.handleSubmit}>
            <Stack spacing="1rem">
              <FormSection mt="2rem" title="Contrôleur qualité">
                <SwitchInput
                  mb="1rem"
                  label="Ignorer le contrôle qualité"
                  name="ignoreQualityControl"
                  id="ignoreQualityControl"
                  yesLabel="Oui"
                  noLabel="Non"
                />
                <SelectInput
                  isDisabled={isQualityControllerDisabled(formik.values)}
                  placeholder="Sélectionner un contrôleur qualité"
                  name="qualityControllerUserId"
                  id="qualityControllerUserId"
                  options={getPositionnableQualityControllers(formik.values)?.map(operator => ({
                    label: `${operator.firstName} ${operator.lastName}`,
                    value: operator.id,
                    ergoQualification: operator.ergoQualification,
                  }))}
                />
              </FormSection>
              <Exclude
                exclude="folder:bloc_a:tab_1:type_of_visit:read"
                no={() => (
                  <FormSection title="Modalité de visite">
                    <LockedInput
                      defaultIsLocked={
                        (!!formik.values.typeOfVisit && formik.values.typeOfVisit !== 'joint') ||
                        isHousingVisitDatePassed(formik.values) ||
                        (formik.values.typeOfVisit === 'joint' && initialValues.isPublished) ||
                        !isQualityControllerFilled(formik.values)
                      }
                      disabled={
                        isTypeOfDisabled ||
                        isHousingVisitDatePassed(formik.values) ||
                        !isQualityControllerFilled(formik.values)
                      }
                      bottom="calc(100% + 18px)"
                      top="auto"
                      left="18ch"
                    >
                      <InterventionModality folderType={folder.folderType} />
                      
                    </LockedInput>
                    {formik.values.typeOfVisit === 'joint' &&
                    !formik.values.positionedCompany ? (
                      <>
                        <RadioGroup
                          isInline
                          mt={5}
                          name="expectedQuotesCount"
                          legend="Nombre de devis entreprise attendus"
                          disabled={initialValues.isPublished}
                        >
                          <RadioInput value={1}>1 devis</RadioInput>
                          <RadioInput value={2}>2 devis</RadioInput>
                        </RadioGroup>
                        <PrimaryButton
                          ml={8}
                          onClick={onOpen}
                          disabled={initialValues.isPublished}
                        >
                          Publier le marché
                        </PrimaryButton>
                      </>
                    ) : null}

                    <CasSwitch companies={filteredCompanies} />

                  

                    {!isTypeOfDisabled &&
                    (initialValues.fioQ1BisAnswer?.includes('A') ||
                      initialValues.fioQ6Answer?.match('B')) ? (
                      // eslint-disable-next-line react/jsx-indent
                      <Notice variant="warning" mt="2rem">
                        <NoticeText variant="warning">
                          Mise à disposition du marché non recommandée :
                        </NoticeText>
                        <NoticeList variant="warning" mx="auto" mt={-3} fontWeight="thin">
                          {initialValues.fioQ1BisAnswer?.includes('A') ? (
                            <li>type de travaux non-défini</li>
                          ) : null}
                          {initialValues.fioQ6Answer?.match('B') ? <li>parc public</li> : null}
                        </NoticeList>
                      </Notice>
                    ) : null}
                  </FormSection>
                )}
              />

              <Exclude
                exclude="folder:bloc_a:tab_1:company_name:read"
                no={() => (
                  <FormSection
                    mt="2rem"
                    title={`Nom de l'entreprise${
                      formik.values?.typeOfVisit === 'existing_quote' ? ' (devis existant)' : ''
                    }`}
                  >
                    <LockedInput
                      defaultIsLocked={
                        !!formik.values.positionedCompany || isHousingVisitDatePassed(formik.values)
                      }
                      disabled={
                        isPositionnedCompanyDisabled(formik.values) ||
                        isHousingVisitDatePassed(formik.values)
                      }
                      bottom="calc(100% + 18px)"
                      top="auto"
                      left={formik.values?.typeOfVisit === 'existing_quote' ? '37ch' : '20ch'}
                    >
                      <SelectInput
                        placeholder="Sélectionner une entreprise"
                        name="positionedCompany"
                        id="positionedCompany"
                        options={filteredCompanies}
                        isDisabled={isPositionnedCompanyDisabled(formik.values)}
                      />
                    </LockedInput>
                    {formik.values.typeOfVisit === 'existing_quote' &&
                    formik.values.positionedCompany ? (
                      <>
                        <Label>Devis</Label>
                        <QuoteUploadWrapper>
                          <QuoteUploadFilename
                            quoteVersion={initialValues.firstQuoteVersion}
                            placeholder="Aucun devis disponible"
                          />
                        </QuoteUploadWrapper>
                      </>
                    ) : null}
                  </FormSection>
                )}
              />

              {isPositionableErgoDisabled(formik.values) ? (
                <Notice variant="warning" mt="2rem">
                  <NoticeText variant="warning">
                    Action requise pour désigner un ergothérapeute
                  </NoticeText>
                  <NoticeThinText mt="-1rem">
                    Veuillez désigner l’entreprise ayant produit le devis ou la facture avant de
                    désigner un ergothérapeute ou mettre la demande d’intervention sur la project
                    place.
                  </NoticeThinText>
                </Notice>
              ) : null}
              <Exclude
                exclude="folder:bloc_a:tab_1:operators:read"
                no={() => (
                  <FormSection
                    title="Opérateurs"
                    mt={isPositionableErgoDisabled(formik.values) ? '0' : '2rem'}
                  >
                    {!isProjectHavingPositionableErgo(formik.values) && (
                      <Notice variant="warning" my="2rem">
                        <NoticeText variant="warning">
                          Attention
                          <br />
                          Aucun ergothérapeute n'ayant les accréditations nécessaires sur ce
                          territoire n'est trouvé.
                        </NoticeText>
                        <NoticeList variant="warning" mx="auto" mt={-3} fontWeight="thin">
                          <li>Aucun ergothérapeute ne pourra se positionner.</li>
                          <li>
                            Veuillez avertir le responsable de recrutement des ergothérapeutes.
                          </li>
                        </NoticeList>
                      </Notice>
                    )}
                    <PrimaryButton
                      mb={8}
                      onClick={onPublishErgoProjectPlaceModalOpen}
                      disabled={isPublishOnErgoProjectPlaceDisabled(formik.values)}
                    >
                      Publier sur la project place ergo
                    </PrimaryButton>
                    <SelectInput
                      label="Ergothérapeute référent"
                      name="ergoUserId"
                      id="ergoUserId"
                      isDisabled={isPositionableErgoDisabled(formik.values)}
                      isClearable
                      options={getPositionnableErgoUsers(formik.values)?.map(operator => ({
                        label: `${operator.firstName} ${operator.lastName}`,
                        value: operator.id,
                        ergoQualification: operator.ergoQualification,
                      }))}
                      placeholder="Sélectionner un opérateur"
                      noOptionsMessage={() => 'Aucun ergothérapeute disponible'}
                      postOnChange={ergo => {
                        formik.setFieldValue('ergoUserKind', getErgoQualification(ergo));
                      }}
                    />
                    <RadioGroup
                      isInline
                      name="ergoUserKind"
                      legend="Ergothérapeute interne/externe"
                      disabled
                    >
                      <RadioInput value="interne">Interne</RadioInput>
                      <RadioInput value="externe">Externe</RadioInput>
                    </RadioGroup>
                    <Can
                      perform="dashboard:visit"
                      yes={() => (
                        <TextInput
                          label="Date de positionnement"
                          name="ergoPositionedAt"
                          id="ergoPositionedAt"
                          disabled
                        />
                      )}
                    />
                  </FormSection>
                )}
              />

              <FormSection title="Préparation de la visite" mt="2rem">
                <DateInput
                  label="Date de prise de contact pour programmation"
                  name="recipientContactedAt"
                  id="recipientContactedAt"
                  disabled={isRecipientContactedAtDisabled(formik.values)}
                />

                <RadioGroup
                  isInline
                  name="isRecipientContacted"
                  legend="Une visite a-t-elle pu être programmée ?"
                  disabled={isIsRecipientContactedDisabled(formik.values)}
                >
                  <RadioInput value="yes">Oui</RadioInput>
                  <RadioInput value="attempts">
                    1 à 3 tentatives de contact restées infructueuses
                  </RadioInput>
                  <RadioInput disabled={!formik.values.canSetDifficulty} value="difficulties">
                    Je rencontre des difficultés à contacter le bénéficiaire
                  </RadioInput>
                </RadioGroup>

                <Stack isInline spacing="1rem" align="flex-start">
                  <DateInput
                    label="Date de visite à domicile"
                    name="housingVisitedAt"
                    id="housingVisitedAt"
                    requiredInput={formik.values.isRecipientContacted === 'yes'}
                    enabledFutureDates
                    disabled={isHousingVisitedAtDisabled(formik.values)}
                  />
                  <TextInput
                    label="Heure de visite à domicile"
                    name="housingVisitedAtTime"
                    id="housingVisitedAtTime"
                    type="time"
                    width="11ch"
                    requiredInput={isHousingVisitedAtTimeRequired(formik.values)}
                    disabled={isHousingVisitedAtDisabled(formik.values)}
                  />
                </Stack>

                <Exclude
                  exclude="folder:bloc_a:tab_1:visit_preparation:visit_reminder_sent_at:read"
                  no={() => (
                    <DateInput
                      label="Date d'envoi de la lettre de rappel de visite"
                      name="visitReminderSentAt"
                      id="visitReminderSentAt"
                      disabled={isVisitReminderSentAtDisabled(formik.values)}
                    />
                  )}
                />
              </FormSection>
            </Stack>
            <ConfirmationModal
              isOpen={isOpen}
              onClose={onModalClose}
              success={success}
              onSubmit={() => {
                formik.setFieldValue('isPublishedInProjectPlace', true);
                formik.submitForm().then(() => setSuccess(true));
              }}
              pending={pending || formik.isSubmitting}
            />
            <ConfirmationPublishErgoModal
              isOpen={isPublishErgoProjectPlaceModalOpen}
              onClose={onPublishErgoProjectPlaceModalClose}
              folderType={folder.folderType}
              typeOfVisit={formik.values.typeOfVisit}
              success={success}
              onSubmit={() => {
                if (!isPublishErgoProjectPlaceModalNeeded)
                  formik.setFieldValue('ergoInterventionType', 'AMOc - ergo seul');
                formik.setFieldValue('isPublishedOnErgoProjectPlace', true);
                formik.submitForm().then(() => setSuccess(true));
              }}
              pending={pending || formik.isSubmitting}
            />

            <FormSubmitButton
              type="submit"
              disabled={
                pending ||
                isHousingVisitDateMissing ||
                isHousingVisitTimeMissing
              }
              isLoading={formik.isSubmitting}
              loadingText="Enregistrer"
            >
              {isHousingVisitDateMissing || isHousingVisitTimeMissing ? (
                <Tooltip
                  bg="semiDarkGray.900"
                  aria-label={
                    isHousingVisitDateMissing
                      ? 'Veuillez renseigner la date de visite à domicile'
                      : "Veuillez renseigner l'heure de visite à domicile"
                  }
                  label={
                    isHousingVisitDateMissing
                      ? 'Veuillez renseigner la date de visite à domicile'
                      : "Veuillez renseigner l'heure de visite à domicile"
                  }
                  placement="top"
                >
                  Enregistrer
                </Tooltip>
              ) : (
                'Enregistrer'
              )}
            </FormSubmitButton>
          </TabForm>
        );
      }}
    </BlocFormik>
  );
};

const CasSwitch = ({ companies }) => {
  const { values, setValues } = useFormikContext();

  const typeOfVisit = usePrevious(values.typeOfVisit);

  React.useEffect(() => {
    if (
      !!typeOfVisit &&
      typeOfVisit !== values.typeOfVisit &&
      !companies.find(company => company.value === values.positionedCompany?.value)
    ) {
      setValues({ ...values, positionedCompany: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.typeOfVisit, setValues]);
  return null;
};

const ConfirmationModal = ({ isOpen, onSubmit, pending, success, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent rounded="lg">
        <ModalHeader>Confirmation de la publication</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          {success ? (
            <p>Le marché a bien été publié. Vous pouvez le retrouver dans la Project Place.</p>
          ) : (
            <p>Êtes-vous certain de vouloir publier ce marché ?</p>
          )}
        </ModalBody>

        <ModalFooter>
          {success ? (
            <PrimaryButton onClick={onClose}>OK</PrimaryButton>
          ) : (
            <>
              <SecondaryButton mr={3} onClick={onClose}>
                Non
              </SecondaryButton>
              <PrimaryButton
                onClick={onSubmit}
                isLoading={pending}
                loadingText="Publication en cours..."
              >
                Oui
              </PrimaryButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const OptionalRadioInput = ({ fieldName, legend, values, setObserver }) => (
  <RadioGroup name={fieldName} legend={legend}>
    {values.map(option => (
      <RadioInput
        key={option.value}
        value={option.value}
        postOnChange={newValue => (setObserver ? setObserver(newValue) : null)}
      >
        {option.label}
      </RadioInput>
    ))}
  </RadioGroup>
);

const InterventionModality = ({ folderType }) => {
  const { setFieldValue } = useFormikContext();
  const [interventionType, setInterventionType] = useState(null);

  const updateTypeOfVisit = (ergoInterventionType) => {
    if (!ergoInterventionType) return;

    if (["AMOc - visite conjointe", "AMOc - diagnostic conjoint", "AMOc - DAL + 2DPE – Conjointe"].includes(ergoInterventionType)) {
      setFieldValue('typeOfVisit', 'joint');
    } else if (["AMOc - ergo seul", "AMOc - diagnostic ergo seul", "AMOc - DAL + 2DPE – Seule"].includes(ergoInterventionType)) {
      setFieldValue('typeOfVisit', 'solo');
    } else if (["AMOc - devis existant", "AMOc - facture existante", "AMOc - diagnostic devis existant", "AMOc - DAL + 2DPE – sur devis"].includes(ergoInterventionType)) {
      setFieldValue('typeOfVisit', 'existing_quote');
    }
  };

  const InterventionSubForm = () => {
    if (folderType === 'AMOe') {
      return (
        <OptionalRadioInput
          fieldName="ergoInterventionType"
          legend="S'agit-il d'une visite aide-ménagère ou d'une visite diagnostic ?"
          values={[
            { label: 'Evaluation des besoins en aide-ménagère', value: 'AMOe - AMD' },
            { label: "Evaluation d'un ergothérapeute sans suite", value: 'AMOe - visite diagnostic' },
          ]}
          setObserver={(value) => {
            setInterventionType(value);
            updateTypeOfVisit(value);
          }}
        />
      );
    }
    
    return (
      <OptionalRadioInput
        fieldName="ergoInterventionType"
        legend="S'agit-il d'une intervention conjointe d'un ergothérapeute ou d'un diagnostic autonomie conjoint ?"
        values={[
          { label: 'Diagnostic autonomie conjoint', value: 'AMOc - diagnostic conjoint' },
          { label: 'Diagnostic autonomie seul', value: 'AMOc - diagnostic ergo seul' },
          { label: 'Diagnostic autonomie sur devis existant', value: 'AMOc - diagnostic devis existant' },
          { label: 'Intervention conjointe ergothérapeute-entreprise', value: 'AMOc - visite conjointe' },
          { label: "Intervention d'un ergothérapeute seul", value: 'AMOc - ergo seul' },
          { label: "Intervention d'un ergothérapeute sur devis existant", value: 'AMOc - devis existant' },
          { label: "Intervention d'un ergothérapeute sur facture existante", value: 'AMOc - facture existante' },
          { value: 'AMOc - DAL + 2DPE – Seule', label: 'DAL + 2DPE – Seule' },
          { value: 'AMOc - DAL + 2DPE – Conjointe', label: 'DAL + 2DPE – Conjointe' },
          { value: 'AMOc - DAL + 2DPE – sur devis', label: 'DAL + 2DPE – sur devis' },
        ]}
        setObserver={(value) => {
          setInterventionType(value);
          updateTypeOfVisit(value);
        }}
      />
    );
  };

  return (
    <InterventionSubForm />
  );
};

const ConfirmationPublishErgoModal = ({
  isOpen,
  onSubmit,
  pending,
  success,
  onClose,
  isInterventionChoiceNeeded,
}) => {
  const [interventionType, setInterventionType] = useState(null);

  return (
    <Modal isOpen={isOpen} onClose={() => {}}>
      <ModalOverlay />
      <ModalContent rounded="lg">
        <ModalHeader>Confirmation de la publication</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} onClick={onClose} />
        <ModalBody>
          {success && (
            <p>Le projet a été publié. Vous pouvez le retrouver dans la project place ergo.</p>
          )}
          {!success && (
            <p>Êtes-vous certain de vouloir publier sur la project place ergo ?</p>
          )}
        </ModalBody>

        <ModalFooter>
          {success ? (
            <PrimaryButton onClick={onClose}>OK</PrimaryButton>
          ) : (
            <>
              <SecondaryButton mr={3} onClick={onClose}>
                Annuler
              </SecondaryButton>
              <PrimaryButton
                onClick={onSubmit}
                isLoading={pending}
                loadingText="Publication en cours..."
                disabled={isInterventionChoiceNeeded && !interventionType}
              >
                Confirmer
              </PrimaryButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
